import { toWSDateString } from "@wingspanhq/fe-component-library";
import { InvoiceRow } from "@wingspanhq/wingspan-api-sdk";
import isEmpty from "lodash/isEmpty";
import { selectorInvoiceRowClientName } from "../selectors/selectorInvoiceRowClientName";

/**
 * Map a v3 invoice search row to a CSV record. Rewritten for the v3
 * `InvoiceRow`: it exports the fields the row carries (identity, amount,
 * lifecycle status + approval/review, event dates, line items).
 *
 * Accepted regression (V3_MIGRATION_TRACKER.md): the v1 CSV columns backed by
 * data no longer on the row are dropped — member gross/net + processing fee +
 * tax-withheld (`amountDetails` / payout destinations), parent-invoice / member-
 * client ids, PO number, project name, invoice notes / member comment, invoice
 * type label, attachment links, and the sent/disputed/declined/approved/
 * estimated-deposit event dates.
 */
export const mapInvoiceToCsvTable = (invoice: InvoiceRow) => {
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const lineItems = invoice.lineItems ?? [];

  return {
    "Invoice ID": invoice.id,
    "Client Name": selectorInvoiceRowClientName(invoice).primaryName,
    "Client Email": invoice.email ?? undefined,
    Currency: invoice.currency?.toUpperCase(),
    Amount: invoice.amount,
    "Due Date": invoice.dueDate
      ? toWSDateString(invoice.dueDate, "input")
      : undefined,
    "Created Date": invoice.events?.createdAt
      ? toWSDateString(invoice.events.createdAt)
      : undefined,
    "Updated Date": invoice.events?.updatedAt
      ? toWSDateString(invoice.events.updatedAt)
      : undefined,
    "Opened Date": invoice.events?.openedAt
      ? toWSDateString(invoice.events.openedAt)
      : undefined,
    "Paid Date": invoice.events?.paidAt
      ? toWSDateString(invoice.events.paidAt)
      : undefined,
    "Deposited Date": invoice.events?.depositedAt
      ? toWSDateString(invoice.events.depositedAt)
      : undefined,
    "Cancelled Date": invoice.events?.cancelledAt
      ? toWSDateString(invoice.events.cancelledAt)
      : undefined,
    Timezone: timezone,
    "Invoice Number": invoice.invoiceNumber,
    Status: invoice.status,
    "Payer Approval Status": invoice.payerApprovalStatus ?? undefined,
    "Payee Review Status": invoice.payeeReviewStatus ?? undefined,
    "Recurring Invoice ID": invoice.recurringInvoiceId ?? undefined,
    "Line Items": isEmpty(lineItems)
      ? undefined
      : JSON.stringify(lineItems)
          // fix json string in csv
          .replace(/"/g, '""'),
    "Line Item Description": isEmpty(lineItems)
      ? undefined
      : lineItems
          .map(l => l.description)
          .filter(Boolean)
          .join(", "),
    "Line Item Amount": isEmpty(lineItems)
      ? undefined
      : lineItems
          .map(l => l.amount?.toFixed(2))
          .filter(Boolean)
          .join(", ")
  };
};
